import { PROJECT_STATUS_WORDS, type ProjectStatus } from './api.ts';

/**
 * A project's status changes only by an event, never chosen (Jason, 2026-10-06: "status should not be selectable, it
 * has to be triggered by a project level event ... like a pause or cancellation require certain things, active is
 * default, archived has to happen after the project is completed, or canceled"; Still to decide, A). Which event a
 * project takes as it stands is one rule, read by the API's status door (domain/status.ts) for what it refuses and by
 * Settings for the buttons it offers, so a button is never offered that the door refuses, or the other way round.
 *
 *   paused      a job running, with why
 *   resumed     a paused job
 *   cancelled   a job that ends before it is finished: while it runs, never once completed, cancelled or archived
 *   completed   Final payment and sign-off done on a job running (or paused); never a lead
 *   archived    a job completed or cancelled, or a lead whenever NSB is done with it
 */

export const STATUS_EVENTS = ['paused', 'resumed', 'cancelled', 'completed', 'archived'] as const;
export type StatusEvent = (typeof STATUS_EVENTS)[number];

/** What the rule reads of a project: its status, and whether it has started (a lead has not). */
export interface StatusFacts {
  status: ProjectStatus;
  started: boolean;
}

/** The status each event leaves a project in. */
export const STATUS_AFTER: Record<StatusEvent, ProjectStatus> = {
  paused: 'paused',
  resumed: 'active',
  cancelled: 'cancelled',
  completed: 'closed',
  archived: 'archived',
};

const running = (s: ProjectStatus) => s === 'active' || s === 'paused';

/** Why the event is refused for the project as it stands, in a sentence; null when it is taken. */
export function statusRefusal(p: StatusFacts, event: StatusEvent): string | null {
  const now = PROJECT_STATUS_WORDS[p.status].toLowerCase();
  switch (event) {
    case 'paused':
    case 'resumed': {
      if (!p.started) return 'A lead is not paused: archive it, or start the project.';
      const from = event === 'paused' ? 'active' : 'paused';
      return p.status === from ? null : `This project is ${now}, not ${from}.`;
    }
    case 'cancelled':
      if (p.status === 'cancelled') return 'This project is already cancelled.';
      if (!p.started) return 'A lead is closed by archiving it, not cancelled.';
      return running(p.status) ? null : `A project is cancelled only while it is running; this one is ${now}.`;
    case 'completed':
      if (!p.started) return 'A lead is completed only once it is started and its work is done.';
      return running(p.status) ? null : `This project is ${now}; only a project still running is completed.`;
    case 'archived':
      if (p.status === 'archived') return 'It is archived already.';
      return !p.started || p.status === 'closed' || p.status === 'cancelled' ? null : `A project is archived once it is completed or cancelled; this one is ${now}.`;
  }
}

/** The events the project takes as it stands, in the order above. */
export function statusEvents(p: StatusFacts): StatusEvent[] {
  return STATUS_EVENTS.filter((e) => statusRefusal(p, e) === null);
}
